import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot, PIXIElement } from "slotworks/graphics/pixi/group";
import ButtonElement, { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Contract from 'slotworks/utils/contract';
import BetOptions from 'slotworks/components/betbar/vo/bet-options';
import currency from 'slotworks/i18n/currency';
import BonusBuyContainer from './bonus-buy-container';
import { JackpotValues } from 'integration/revolver/schema/persisted-progress';
import gameState from 'slotworks/model/game-state';

export default class BonusBuyPopups {

    public container: PIXI.Container;

    private enabled: boolean = false;

    private buttons: ButtonElement[] = [];

    public buyButton: ButtonElement;
    public plusButton: ButtonElement;
    public minusButton: ButtonElement;

    public totalBetText: PIXI.Text;
    public totalValue: PIXI.Text;

    private betOptions: BetOptions;

    private type: string;

    private static TOP_INDEX = 12;

    private static plaque_elements: string[] = [
        "jpbettext", "jpbet", "jpvalue", "jpbuy-lbl"
    ];

    private static plaque_buttons: string[] = [
        "jpnext", "jpprev", "jpbuy"
    ];

    private static plaque_bg_elements: string[] = [
        "jpbuy_bg", "jpfield"
    ]

    constructor(type: string, copy: boolean = false, betOptions: BetOptions, increment: () => void, decrement: () => void, buy: () => void) {
        this.type = type;
        this.betOptions = betOptions;
        const bg = Layers.Custom.get("BonusBuyPopups").getSprite(type + "_Plaque");

        const elements: PIXIElement[] = [bg];

        for(let name of BonusBuyPopups.plaque_bg_elements) {
            let element = Layers.Custom.get("BonusBuyPopups").getSprite(name) as PIXIElement;
            if(copy) {
                element = this.copyElement(element, type);
            }
            elements.push(element);
        }

        for(let name of BonusBuyPopups.plaque_buttons) {
            let button = Layers.Custom.get("BonusBuyPopups").getButton(name);
            if(copy) {
                button = this.copyButton(button, name, type);
            }
            elements.push(button.container);
            this.buttons.push(button);
        }

        for(let name of BonusBuyPopups.plaque_elements) {
            let element = Layers.Custom.get("BonusBuyPopups").getSprite(name) as PIXIElement;
            if(copy) {
                element = this.copyElement(element, type);
            }
            elements.push(element);
        }

        this.container = CenterPivot(elements, "prompt_" + type);
        this.totalBetText = elements.filter(el => el.name == "jpbet" || el.name.includes("jpbet_"))[0] as PIXI.Text;
        this.totalValue = elements.filter(el => el.name.includes("jpvalue"))[0] as PIXI.Text;

        this.buyButton = this.getButton("jpbuy");
        this.minusButton = this.getButton("jpprev");
        this.plusButton = this.getButton("jpnext");

        this.buyButton.on(ButtonEvent.POINTER_DOWN, buy);
        this.minusButton.on(ButtonEvent.POINTER_DOWN, decrement);
        this.plusButton.on(ButtonEvent.POINTER_DOWN, increment);

        this.moveToTop();
    }

    public setBetOptions(betOptions: BetOptions) {
        this.betOptions = betOptions;
    }

    public moveToTop() {
        Layers.Custom.get("BonusBuyPopups").container.setChildIndex(this.container, BonusBuyPopups.TOP_INDEX);
    }

    public destroy() {

    }

    public updateFields() {
        const currentBet = this.betOptions.getTotalStake();
        this.totalBetText.text = currency.format(currentBet);
        this.totalValue.text = currency.format(currentBet * BonusBuyContainer.MULTIPLIER_RESPIN[this.type as keyof JackpotValues]);

        if(this.enabled) {
            if(this.isBalanceAboveAmount()) {
                this.buyButton.setEnabled(true);
            } else {
                this.buyButton.setEnabled(false);
            }
        }
    }

    public setEnabled(enabled: boolean) {
        this.enabled = enabled;
        for(let button of this.buttons) {
            button.setEnabled(enabled);
        }

        if(enabled) {
            if(this.isBalanceAboveAmount()) {
                this.buyButton.setEnabled(enabled);
            } else {
                this.buyButton.setEnabled(false);
            }
        } else {
            this.buyButton.setEnabled(enabled);
        }
    }

    private isBalanceAboveAmount() {
        const currentBet = this.betOptions.getTotalStake();
        const amount = currentBet * BonusBuyContainer.MULTIPLIER_RESPIN[this.type as keyof JackpotValues];
        const balance = gameState.getCurrentGame().balance;
        
        return amount <= balance;
    }

    public zoom(from: number, to: number, time: number, enable: boolean, instant: boolean = false) {
        if(instant) {
            this.container.scale.set(to, to);
            this.setEnabled(enable);
            return Contract.empty();
        }
        return new Contract(resolve => {
            this.setEnabled(false);
            this.container.scale.set(from, from);
            new TweenJS.Tween(this.container.scale)
            .to({x: to, y: to}, time)
            .onComplete(() => {
                this.setEnabled(enable);
                resolve(null);
            })
            .start();
            this.moveToTop();
        });
    }

    public moveTo(x:number, y: number, endScale: number, time: number, enable: boolean): Contract<void> {
        return new Contract(resolve => {
            this.setEnabled(false);
            new TweenJS.Tween(this.container)
            .to({x: x, y: y}, time)
            .onComplete(() => {
                this.setEnabled(enable);
                resolve(null);
            })
            .start();
            new TweenJS.Tween(this.container.scale)
            .to({x: endScale, y: endScale}, time - 10)
            .start();
        });  
    }

    private getButton(name: string) {
        return this.buttons.filter(button => button.container.name.includes(name))[0];
    }

    private copyElement(element: PIXIElement, type: string) {
        let res: PIXIElement = null;

        if(element instanceof PIXI.Sprite) {
            const sprite = new PIXI.Sprite(element.texture);
            sprite.scale.set(element.scale.x, element.scale.y);
            res = sprite;
        } else if(element instanceof PIXI.Text) {
            res = new PIXI.Text(element.text, element.style);
        } else if(element instanceof PIXI.BitmapText) {
            res = new PIXI.BitmapText(element.text, { fontName: element.fontName, fontSize: element.fontSize});
        }
        const x = (element as PIXI.Sprite).anchor.x;
        const y = (element as PIXI.Sprite).anchor.y;
        (res as PIXI.Sprite).anchor.set(x, y);
        res.name = element.name + "_" + type;
        Layers.Custom.get("BonusBuyPopups").add(res);
        res.x = element.x;
        res.y = element.y;
        return res;
    }

    private copyButton(button: ButtonElement, name: string, type: string) {
        const container = new PIXI.Container();
        container.name = "btn-" + name + "_" + type;

        Layers.Custom.get("BonusBuyPopups").container.addChild(container);
        const newButton = new ButtonElement(container);
        Layers.Custom.get("BonusBuyPopups").addButton(container.name, newButton);

        for(let buttonSprite of button.container.children) {
            const bSprite = buttonSprite as PIXI.Sprite;
            const sprite = new PIXI.Sprite(bSprite.texture);

            const idParts = bSprite.name.split("-");
            const buttonState = idParts.length > 1 ? idParts[1] : "up";

            sprite.width = bSprite.width;
            sprite.height = bSprite.height;
            newButton.addState(buttonState, sprite);
            sprite.x = bSprite.x;
            sprite.y = bSprite.y;

            const spriteName = "btn-" + container.name + buttonState;
            
            sprite.name = spriteName + "_" + type;
        }
        container.x = button.container.x;
        container.y = button.container.y;

        return newButton;
    }

}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/bonus-buy/bonus-buy-prompt.ts